import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { HardHat, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  useSeo({ title: "Wachtwoord vergeten | Work4you", description: "Vraag een herstellink aan om je Work4you-wachtwoord opnieuw in te stellen.", path: "/wachtwoord-vergeten", noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 bg-orange-500 flex items-center justify-center rounded-sm">
            <HardHat className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold">Work4you</span>
        </Link>

        {sent ? (
          <div data-testid="forgot-sent-message" className="bg-white border border-slate-200 rounded-md p-8 text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MailCheck className="w-6 h-6 text-green-600" />
            </div>
            <h1 className="font-display text-2xl font-extrabold text-slate-950 mb-2">Controleer je e-mail</h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              Als er een account bestaat met <strong>{email}</strong>, sturen we een herstel-link.
              De link is 1 uur geldig.
            </p>
            <Link to="/inloggen" data-testid="back-to-login-link" className="inline-block mt-6 text-orange-600 font-semibold hover:underline text-sm">
              Terug naar inloggen
            </Link>
          </div>
        ) : (
          <>
            <p className="label-caps mb-2">Wachtwoord vergeten</p>
            <h1 className="font-display text-3xl font-extrabold text-slate-950 mb-3">Herstel je wachtwoord</h1>
            <p className="text-sm text-slate-600 mb-8">
              Vul je e-mailadres in. We sturen je een link om een nieuw wachtwoord in te stellen.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs font-bold text-slate-700">E-mailadres</Label>
                <Input data-testid="forgot-email-input" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-sm border-slate-300" placeholder="naam@bedrijf.nl" />
              </div>
              <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-11">
                {loading ? "Bezig..." : "Stuur herstel-link"}
              </Button>
            </form>
            <p className="text-center text-sm text-slate-500 mt-6">
              <Link to="/inloggen" data-testid="back-to-login-link" className="text-orange-600 font-semibold hover:underline">
                Terug naar inloggen
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
